// utils/quotationPaths.js
// ----------------------------------------------------------------------
// Every quotation-related page previously hand-rolled its own
// if (role === "admin") ... else if (role === "engineer") ... chain to
// build a path. That's why the Print button and "Back to List" button
// pointed at routes that don't exist in App.js (/quotations/print/:id,
// /quotations) — nobody could see all the copies at once to keep them
// in sync. Centralizing it here means there's exactly one place that
// knows the real route structure.
// ----------------------------------------------------------------------

const ROLE_PREFIX = {
  admin: "/admin",
  engineer: "/engineer",
  staff: "/staff",
};

// Only admin/engineer/staff have a quotations module registered in
// App.js today. Returns null for anyone else so callers can decide to
// hide/disable an action instead of navigating to a dead route.
export function getQuotationBase(role) {
  return ROLE_PREFIX[String(role || "").toLowerCase()] || null;
}

export function getDashboardPath(role) {
  const r = String(role || "").toLowerCase();
  const base = getQuotationBase(r);
  if (base) return `${base}/dashboard`;
  if (r === "storekeeper") return "/store/dashboard";
  if (r === "apprentice") return "/apprentice/dashboard";
  return "/";
}

/**
 * @param {string} role
 * @param {"dashboard"|"list"|"create"|"view"|"edit"|"print"} action
 * @param {string|number} [id] required for view/edit/print
 * @returns {string|null} the path, or null if this role has no quotations module
 */
export function getQuotationPath(role, action, id) {
  const base = getQuotationBase(role);
  if (!base) return null;

  switch (action) {
    case "dashboard":
      return `${base}/quotations`;
    case "list":
      return `${base}/quotations/list`;
    case "create":
      return `${base}/quotations/create`;
    case "view":
      return `${base}/quotations/view/${id}`;
    case "edit":
      return `${base}/quotations/edit/${id}`;
    case "print":
      return `${base}/quotations/print/${id}`;
    default:
      return `${base}/quotations`;
  }
}

// Standard "who can approve/edit this quotation" checks, matching what
// the backend actually enforces in quotations.js — keeping this in one
// place stops the frontend from silently drifting out of sync with it
// again (which is exactly how the Approve-button bug happened).
export function canApproveQuotation(user, quotation) {
  if (!user || !quotation) return false;
  const role = String(user.role || "").toLowerCase();
  return role === "admin" || user.email === quotation.created_by;
}

export function canEditQuotation(user, quotation) {
  if (!user || !quotation) return false;
  const role = String(user.role || "").toLowerCase();
  return role === "admin" || user.email === quotation.created_by;
}
